import React, { FC, useEffect, useMemo } from "react";
import { Checkbox } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { Notice } from "../../CopyResource/components/Project/styled";
import { ConfirmText, Content } from "../styled";
import { Loading } from "./Loading";

import { projectInfosWithDeps } from "../../../../resources/projects";
import { useResourcesLimits } from "../../CopyResource/hooks/useResourcesLimits";
import { useSelectedResourcesNames } from "../../CopyResource/hooks/useSelectedResourcesNames";

import { BulkCopyOptionsProps } from "../../CopyResource/types";

// eslint-disable-next-line max-lines-per-function
export const BulkCopyProjectsContent: FC<BulkCopyOptionsProps> = ({
  copyLayers,
  copyTables,
  toggleLayers,
  toggleTables,
  onLoading,
  limits,
}) => {
  const { t } = useTranslation("dialogs");

  const {
    setTablesTotalCount,
    setLayersTotalCount,
    isInitialTablesLimit,
    isInitialLayersLimit,
    isTablesLimitExceeded,
    isLayersLimitExceeded,
  } = useResourcesLimits(limits);

  const projectsToCopy: string[] = useSelectedResourcesNames();
  const { data: info, loading } = projectInfosWithDeps.use({ input: projectsToCopy });
  const tablesCount: number = (info && info.deps && info.deps.tables && info.deps.tables.length) || 0;
  const layersCount: number = (info && info.deps && info.deps.layers && info.deps.layers.length) || 0;
  const isAnyDep: boolean = tablesCount + layersCount > 0;

  const confirmText = useMemo(
    () =>
      isAnyDep
        ? `${t("bulkCopy.copyMapsConfirmText1")} ${layersCount > 0 ? t("bulkCopy.layers") : ""} ${
            layersCount > 0 && tablesCount > 0 ? t("bulkCopy.and") : ""
          } ${tablesCount > 0 ? t("bulkCopy.tables") : ""}.
      ${t("bulkCopy.copyMapsConfirmText2")}`
        : t("bulkCopy.copyMapsConfirmText3"),
    [isAnyDep, layersCount, t, tablesCount],
  );

  useEffect(() => {
    isInitialTablesLimit && copyTables && toggleTables();
    isInitialLayersLimit && copyLayers && toggleLayers();
  }, [isInitialTablesLimit, isInitialLayersLimit]);

  useEffect(() => {
    if (limits) {
      setTablesTotalCount(limits.currentTableCount + (copyTables ? tablesCount : 0));
      setLayersTotalCount(limits.currentServiceCount + (copyLayers ? layersCount : 0));
    }
  }, [limits, copyTables, copyLayers, tablesCount, layersCount]);

  useEffect(() => onLoading(loading), [onLoading, loading]);

  return (
    <Content>
      {loading ? (
        <Loading />
      ) : (
        <>
          {!isInitialLayersLimit ? (
            <ConfirmText>{confirmText}</ConfirmText>
          ) : (
            <Notice info>
              {t("bulkCopy.layersLimitReached")} ({limits.currentServiceCount} {t("bulkCopy.of")}{" "}
              {limits && limits.maxServicesCount}
              ). {t("bulkCopy.newMapWillUseOriginalResources")}
            </Notice>
          )}
          {!isInitialLayersLimit ? (
            <>
              {isAnyDep ? (
                <>
                  <Checkbox
                    checked={copyLayers}
                    label={`${t("bulkCopy.copyLayers")} (${layersCount})`}
                    onChange={() => {
                      if (copyLayers && copyTables) {
                        toggleTables();
                      }

                      toggleLayers();
                    }}
                  />
                  {!isInitialTablesLimit ? (
                    <Checkbox
                      disabled={!copyLayers}
                      checked={copyTables}
                      label={`${t("bulkCopy.copyTables")} (${tablesCount})`}
                      onChange={toggleTables}
                    />
                  ) : (
                    <Notice info style={{ marginTop: "1rem" }}>
                      {t("bulkCopy.tablesLimitReached")} ({limits.currentTableCount} {t("bulkCopy.of")}{" "}
                      {limits && limits.maxTablesCount}
                      ). {t("bulkCopy.newLayersWillUseOriginalData")}
                    </Notice>
                  )}
                </>
              ) : null}
              {limits &&
              ((!isInitialTablesLimit && isTablesLimitExceeded) || (!isInitialLayersLimit && isLayersLimitExceeded)) ? (
                <Notice warning>
                  {t("bulkCopy.youHaveGot")}{" "}
                  {isLayersLimitExceeded
                    ? `${limits.currentServiceCount} ${t("bulkCopy.of")} ${limits.maxServicesCount} ${t(
                        "bulkCopy.ofAvailableLayers",
                      )}`
                    : ""}
                  {isTablesLimitExceeded && isLayersLimitExceeded ? ` ${t("bulkCopy.and")} ` : ""}
                  {isTablesLimitExceeded
                    ? `${limits.currentTableCount} ${t("bulkCopy.of")} ${limits.maxTablesCount} ${t(
                        "bulkCopy.ofTables",
                      )}`
                    : ""}
                  , {t("bulkCopy.notAll")}{" "}
                  {isTablesLimitExceeded && isLayersLimitExceeded ? t("bulkCopy.resources") : t("bulkCopy.layers")}{" "}
                  {t("bulkCopy.willBeCopied")}
                </Notice>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </Content>
  );
};
